
import {
    Box,
    Typography,
    Button,
    CircularProgress,
} from '@mui/material';
import { Img_1, Img_2 } from "../../assets";
import { useAppContext } from '../../context/AppContext';
import Layout from '../../components/Layout';
import '../../index.css';
import { useState, useEffect } from 'react';
import ApiService from '../../services/api';
import { getDistributorInfoAndName } from '../../utils/tokenUtils';

const Welcome = () => {
    const { setCurrentView, userProfilesCache, getTranslation, getAllUserSessions, userId, token, sessionId } = useAppContext();
    const [sessionLength, setSessionLength] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Fetch user sessions to check length
    useEffect(() => {
        const fetchSessions = async () => {
            if (token && userId) {
                try {
                    setIsLoading(true);
                    const apiService = new ApiService(null, token);
                    const sessions = await getAllUserSessions(apiService, userId);
                    setSessionLength(sessions.length);
                } catch (error) {
                    console.error('Error fetching user sessions:', error);
                    setSessionLength(0);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };

        fetchSessions();
    }, [token, userId, getAllUserSessions]);

    const isSessionPending = !sessionId;

    const customerCards = [
        {
            title: getTranslation("Txt271"),
            description: getTranslation("Txt406"),
            image: Img_1,
            buttonText: getTranslation("Txt273"),
            action: () => setCurrentView('user-info'),
            disabled: isSessionPending,
            loading: isSessionPending,
        },
        {
            title: getTranslation("Txt274"),
            description: getTranslation("Txt275"),
            image: Img_2,
            buttonText: getTranslation("Txt276"),
            action: () => setCurrentView('MoreUserDetails'),
            disabled: isSessionPending || isLoading || sessionLength === 0,
            loading: isSessionPending || isLoading,
        }
    ];


    return (
        <Layout showBackButton={false}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { 
                        xs: "40px", 
                        sm: "45px",
                        md: "20px",
                        lg: "25px",
                        xl: "30px"
                    },
                    alignItems: "center",
                    justifyContent: { xs: "space-between", md: "flex-start" },

                    maxWidth: { 
                        md: "1200px",
                        lg: "1400px",
                        xl: "1600px"
                    },
                    margin: "0 auto",
                    // minHeight: "100vh",      // Ensure minimum viewport height
                    width: "100%",
                    paddingTop: { 
                        xs: "20px",
                        sm: "20px",
                        md: "10px",
                        lg: "10px",
                        xl: "10px"
                    },
                    paddingBottom: { xs: "40px", sm: "40px", md: "40px" },   // Add bottom padding for scroll space
                    // overflow: "auto"         // Allow scrolling when content exceeds viewport
                }}>
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { 
                        xs: "20px", 
                        sm: "22px",
                        md: "30px",
                        lg: "35px",
                        xl: "40px"
                    },
                    alignItems: { xs: "center", md: "center" },
                    justifyContent: { xs: "flex-start", md: "flex-start" },
                    width: "100%",
                    flex: 1,
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: { 
                            xs: "24px", 
                            sm: "26px",
                            md: '25px',
                            lg: '30px',
                            xl: '35px'
                        },
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: { 
                                xs: "12px", 
                                sm: "12px",
                                md: '8px',
                                lg: '10px',
                                xl: '12px'
                            },
                        }}>
                            <Typography variant="h5" component="h1" sx={{
                                color: "var(--color-text-primary)",
                                textAlign: "center",
                                fontSize: { 
                                    xs: "20px", 
                                    sm: "20px",
                                    md: "24px",
                                    lg: "28px",
                                    xl: "32px"
                                  },
                                  fontStyle: "normal",
                                  fontWeight: { xs: 600, sm: 600, md: 600, lg: 600 },
                                lineHeight: { 
                                    xs: "1.3", 
                                    sm: "1.3",
                                    md: "1.3", 
                                    lg: "1.2", 
                                    xl: "1.1" 
                                },
                                letterSpacing: { 
                                    xs: "0px",
                                    sm: "0px",
                                    md: "0.3px", 
                                    lg: "0.5px", 
                                    xl: "0.8px" 
                                },
                                marginBottom: { 
                                    xs: "8px",
                                    sm: "8px",
                                    md: "6px", 
                                    lg: "10px", 
                                    xl: "15px" 
                                },
                                fontFamily: "HerbalifeFont"
                            }}>
                                {getTranslation("Txt266")}
                            </Typography>
                            <Typography
                                sx={{
                                    color: "var(--color-text-secondary)",
                                    textAlign: "center",
                                    fontSize: { 
                                        xs: "15px", 
                                        sm: "15px",
                                        md: "16px",
                                        lg: "18px",
                                        xl: "20px"
                                    },
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: { 
                                        xs: "1.4", 
                                        sm: "1.4",
                                        md: "1.4", 
                                        lg: "1.3", 
                                        xl: "1.2" 
                                    },
                                    letterSpacing: { 
                                        xs: "0px",
                                        sm: "0px",
                                        md: "0.2px", 
                                        lg: "0.3px", 
                                        xl: "0.5px" 
                                    },
                                    maxWidth: { 
                                        xs: "100%",
                                        sm: "100%",
                                        md: "480px",     // 1280x720 - compact width
                                        lg: "580px",     // 1366x657 - moderate width
                                        xl: "750px"      // 1920x950 - generous width
                                    },
                                    fontFamily: "HerbalifeFont"
                                }}
                            >
                                {getTranslation("Txt267")}
                            </Typography>
                        </Box>

                        <Box sx={{
                            width: '100%',
                            maxWidth: { 
                                xs: '400px', 
                                sm: '420px',
                                md: '450px',     // 1280x720 - compact distributor box
                                lg: '550px',     // 1366x657 - moderate distributor box
                                xl: '680px'      // 1920x950 - generous distributor box
                            },
                            padding: { 
                                xs: '18px 16px', 
                                sm: '18px 18px',
                                md: '20px 25px',
                                lg: '25px 30px',
                                xl: '30px 35px'
                            },
                            borderRadius: { 
                                xs: '12px', 
                                sm: '12px',
                                md: '14px', 
                                lg: '18px', 
                                xl: '22px' 
                            },
                            backgroundColor: '#ffffff',
                            boxShadow: { 
                                xs: '0 2px 8px rgba(0, 0, 0, 0.1)', 
                                md: '0 3px 12px rgba(0, 0, 0, 0.08)', 
                                lg: '0 4px 16px rgba(0, 0, 0, 0.1)', 
                                xl: '0 6px 24px rgba(0, 0, 0, 0.12)' 
                            },
                            border: { md: '1px solid #f0f0f0' }
                        }}>
                            {userProfilesCache.data && (() => {
                                const { distributorName } = getDistributorInfoAndName(userProfilesCache.data);
                                return (
                                    <Typography sx={{
                                        color: 'var(--color-text-primary)',
                                        textAlign: 'center',
                                        fontSize: { 
                                            xs: '14px', 
                                            sm: '14px',
                                            md: '14px',
                                            lg: '16px',
                                            xl: '18px'
                                        },
                                        fontWeight: { xs: 400, sm: 400, md: 500 },
                                        lineHeight: { xs: '1.5', sm: '1.5', md: '1.5' },
                                        marginBottom: { 
                                            xs: '14px', 
                                            sm: '14px',
                                            md: '10px', 
                                            lg: '12px', 
                                            xl: '16px' 
                                        },
                                        letterSpacing: { 
                                            xs: '0px',
                                            sm: '0px',
                                            md: '0.2px', 
                                            lg: '0.3px', 
                                            xl: '0.4px' 
                                        },
                                        fontFamily: 'HerbalifeFont'
                                    }}>
                                        {getTranslation("Txt75").replace(/\[.*?\]/g, distributorName)}
                                    </Typography>
                                );
                            })()}
                            
                        {userProfilesCache.data && (() => {
                            const { distributorInfo } = getDistributorInfoAndName(userProfilesCache.data);
                            return distributorInfo;
                        })() && (
                        
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: { xs: '6px', sm: '6px', md: '20px' },
                                    fontSize: { 
                                        xs: '14px', 
                                        sm: '14px',
                                        md: '14px',
                                        lg: '14px',
                                        xl: '16px'
                                    },
                                    color: 'var(--color-text-secondary)',
                                    padding: { 
                                        xs: '8px 0',
                                        sm: '8px 0',
                                        md: '8px 0', 
                                        lg: '10px 0', 
                                        xl: '12px 0' 
                                    }
                                }}>
                                                                 {(() => {
                                        const { distributorInfo } = getDistributorInfoAndName(userProfilesCache.data);

                                        return distributorInfo ? (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: { xs: '10px', sm: '10px' }
                                            }}>
                                                <Typography sx={{
                                                    fontSize: 'inherit',
                                                    color: 'inherit',
                                                    fontFamily: 'HerbalifeFont',
                                                    textAlign: 'center',
                                                    fontWeight: { xs: 500, sm: 500, md: 500 },
                                                    letterSpacing: { 
                                                        xs: '0px',
                                                        sm: '0px',
                                                        md: '0.2px', 
                                                        lg: '0.3px', 
                                                        xl: '0.4px' 
                                                    }
                                                }}>
                                                    <strong>{getTranslation("Txt825")+":"}</strong> {distributorInfo.primary_email}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: 'inherit',
                                                    color: 'inherit',
                                                    fontFamily: 'HerbalifeFont',
                                                    textAlign: 'center',
                                                    fontWeight: { xs: 500, sm: 500, md: 500 },
                                                    letterSpacing: { 
                                                        xs: '0px',
                                                        sm: '0px',
                                                        md: '0.2px', 
                                                        lg: '0.3px', 
                                                        xl: '0.4px' 
                                                    }
                                                }}>
                                                    <strong>{getTranslation("Txt77").split(':')[0]+":"}</strong> {distributorInfo.phone_number}
                                                </Typography>
                                            </Box>
                                        ) : null;
                                    })()}
                                </Box>
                        )}
                        </Box>

                        {/* Mobile Design (xs, sm) */}
                        <Box sx={{
                            width: '100%',
                            // maxWidth: '320px',
                            padding: '0',
                            display: { xs: 'block', sm: 'block', md: 'none' }
                        }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr',
                                gridAutoRows: '1fr',
                                gap: { xs: '20px', sm: '22px' },
                                width: '100%',
                                alignContent: 'stretch'
                            }}>
                                {customerCards.map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            padding: { xs: '18px', sm: '20px' },
                                            borderRadius: { xs: '12px', sm: '12px' },
                                            backgroundColor: '#f8f8f8',
                                            boxShadow: { xs: '0 2px 8px rgba(0, 0, 0, 0.1)', sm: '0 2px 8px rgba(0, 0, 0, 0.1)' },
                                            gap: { xs: '18px', sm: '20px' },
                                            justifyContent: 'space-between',
                                            minHeight: '100%'
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            gap: { xs: '16px', sm: '18px' }
                                        }}>
                                            <Box
                                                sx={{
                                                    width: { xs: '56px', sm: '60px' },
                                                    height: { xs: '56px', sm: '60px' },
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                    border: { xs: '2px solid #e0e0e0', sm: '2px solid #e0e0e0' }
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={customerCards[index].image}
                                                    alt={customerCards[index].title}
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: { xs: '8px', sm: '8px' }
                                            }}>
                                                <Typography
                                                    sx={{
                                                        color: 'var(--color-text-primary)',
                                                        fontSize: { xs: '17px', sm: '18px' },
                                                        fontWeight: 600,
                                                        lineHeight: { xs: '1.3', sm: '1.3' }
                                                    }}
                                                >
                                                    {customerCards[index].title}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color: 'var(--color-text-secondary)',
                                                        fontSize: { xs: '15px', sm: '15px' },
                                                        fontWeight: 400,
                                                        lineHeight: { xs: '1.4', sm: '1.4' }
                                                    }}
                                                >
                                                    {customerCards[index].description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            width: '100%'
                                        }}>
                                            <Button
                                                variant="contained"
                                                onClick={customerCards[index].action}
                                                disabled={customerCards[index].disabled}
                                                sx={{
                                                    padding: { xs: '10px 20px', sm: '10px 22px' },
                                                    borderRadius: { xs: '74px', sm: '74px' },
                                                    background: customerCards[index].disabled ? '#ccc' : 'var(--color-primary)',
                                                    fontSize: { xs: '15px', sm: '15px' },
                                                    color: '#FFF',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    minWidth: { xs: '110px', sm: '120px' },
                                                    height: { xs: '40px', sm: '42px' },
                                                    '&:hover': {
                                                        background: customerCards[index].disabled ? '#ccc' : 'var(--color-primary-hover)',
                                                    },
                                                }}
                                            >
                                                {customerCards[index].loading ? (
                                                    <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                    customerCards[index].buttonText
                                                )}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Desktop Design (md and up) */}
                        <Box sx={{
                            display: { xs: 'none', sm: 'none', md: 'flex' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            padding: { 
                                md: '15px 0',
                                lg: '20px 0',
                                xl: '25px 0'
                            }
                        }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
                                gap: { 
                                    xs: '20px', 
                                    sm: '22px',
                                    md: '25px',
                                    lg: '30px',
                                    xl: '35px'
                                },
                                justifyContent: 'center',
                                alignItems: 'stretch',  // Stretch cards to match tallest card height
                                maxWidth: { 
                                    md: '850px',        // 1280x720 - compact grid
                                    lg: '1000px',       // 1563x730 - moderate grid
                                    xl: '1300px'        // 1920x950 - spacious grid
                                },
                                width: '100%'
                            }}>
                                {customerCards.map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                                alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            padding: { 
                                                xs: '20px', 
                                                sm: '22px',
                                                md: '25px',
                                                lg: '30px',
                                                xl: '35px'
                                            },
                                            borderRadius: { 
                                                xs: '25px', 
                                                sm: '25px',
                                                md: '20px', 
                                                lg: '24px', 
                                                xl: '28px' 
                                            },
                                            border: '1px solid #e8e8e8',
                                            backgroundColor: '#FFF',
                                            boxShadow: { 
                                                xs: "5px 5px 10px 0px rgba(0, 0, 0, 0.15)", 
                                                sm: "5px 5px 10px 0px rgba(0, 0, 0, 0.15)", 
                                                md: "0 6px 20px rgba(0, 0, 0, 0.08)", 
                                                lg: "0 8px 25px rgba(0, 0, 0, 0.1)", 
                                                xl: "0 12px 35px rgba(0, 0, 0, 0.12)" 
                                            },
                                            gap: { 
                                                xs: '20px', 
                                                sm: '22px',
                                                md: '20px',
                                                lg: '25px',
                                                xl: '30px'
                                            },
                                            height: "auto",
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Circular Image */}
                                        <Box
                                            className="card-image"
                                            sx={{
                                                width: { 
                                                    xs: '120px', 
                                                    sm: '120px',
                                                    md: '90px',
                                                    lg: '100px',
                                                    xl: '110px'
                                                },
                                                height: { 
                                                    xs: '120px', 
                                                    sm: '120px',
                                                    md: '90px',
                                                    lg: '100px',
                                                    xl: '110px'
                                                },
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                border: { 
                                                    xs: '3px solid #f0f0f0', 
                                                    sm: '3px solid #f0f0f0',
                                                    md: '3px solid #f0f0f0' 
                                                },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#f8f8f8',
                                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                boxShadow: { 
                                                    md: '0 3px 12px rgba(0, 0, 0, 0.08)', 
                                                    lg: '0 4px 15px rgba(0, 0, 0, 0.1)', 
                                                    xl: '0 5px 18px rgba(0, 0, 0, 0.12)' 
                                                }
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={customerCards[index].image}
                                                alt={customerCards[index].title}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: '50%'
                                                }}
                                            />
                                        </Box>

                                        {/* Title */}
                                        <Typography
                                            sx={{
                                                color: 'var(--color-text-primary)',
                                                textAlign: 'center',
                                                fontFamily: 'HerbalifeFont',
                                                fontSize: { 
                                                    xs: "18px", 
                                                    sm: "18px",
                                                    md: '18px',
                                                    lg: '20px',
                                                    xl: '22px'
                                                },
                                                fontStyle: 'normal',
                                                fontWeight: 600,
                                                lineHeight: { xs: '1.3', sm: '1.3', md: '1.3', lg: '1.2' },
                                                margin: 0,
                                                letterSpacing: { 
                                                    xs: '0px',
                                                    sm: '0px',
                                                    md: '0.3px', 
                                                    lg: '0.5px', 
                                                    xl: '0.7px' 
                                                }
                                            }}
                                        >
                                            {customerCards[index].title}
                                        </Typography>

                                        {/* Description */}
                                        <Typography
                                            sx={{
                                                color: "var(--color-text-secondary)",
                                                textAlign: "center",
                                                fontFamily: "HerbalifeFont",
                                                fontSize: { 
                                                    xs: "15px", 
                                                    sm: "15px",
                                                    md: "14px",
                                                    lg: "15px",
                                                    xl: "16px"
                                                },
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: { 
                                                    xs: "1.5", 
                                                    sm: "1.5",
                                                    md: "1.5", 
                                                    lg: "1.4", 
                                                    xl: "1.3" 
                                                },
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                maxWidth: { 
                                                    xs: '100%',
                                                    sm: '100%',
                                                    md: '250px',        // 1280x720 - compact width
                                                    lg: '300px',        // 1366x657 - moderate width
                                                    xl: '350px'         // 1920x950 - generous width
                                                },
                                                letterSpacing: { 
                                                    xs: '0px',
                                                    sm: '0px',
                                                    md: '0.2px', 
                                                    lg: '0.3px', 
                                                    xl: '0.4px' 
                                                }
                                            }}
                                        >
                                            {customerCards[index].description}
                                        </Typography>

                                        {/* Action Button */}
                                        <Button
                                            className="card-button"
                                            variant="contained"
                                            onClick={customerCards[index].action}
                                            disabled={customerCards[index].disabled}
                                            sx={{
                                                width: { 
                                                    xs: '120px', 
                                                    sm: '130px',
                                                    md: '140px',
                                                    lg: '150px',
                                                    xl: '160px'
                                                },
                                                height: { 
                                                    xs: '40px', 
                                                    sm: '40px',
                                                    md: '40px',
                                                    lg: '42px',
                                                    xl: '45px'
                                                },
                                                borderRadius: '74px',
                                                background: customerCards[index].disabled ? '#ccc' : 'var(--color-primary)',
                                                fontSize: { 
                                                    xs: '15px', 
                                                    sm: '15px',
                                                    md: '14px',
                                                    lg: '15px',
                                                    xl: '16px'
                                                },
                                                color: '#FFF',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontFamily: 'HerbalifeFont',
                                                letterSpacing: { 
                                                    xs: '0px',
                                                    sm: '0px',
                                                    md: '0.3px', 
                                                    lg: '0.4px', 
                                                    xl: '0.6px' 
                                                },
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                boxShadow: customerCards[index].disabled ? 'none' : { 
                                                    xs: 'none',
                                                    sm: 'none',
                                                    md: '0 3px 12px rgba(0, 0, 0, 0.15)', 
                                                    lg: '0 4px 15px rgba(0, 0, 0, 0.2)', 
                                                    xl: '0 5px 18px rgba(0, 0, 0, 0.25)' 
                                                },
                                                '&:hover': {
                                                    background: customerCards[index].disabled ? '#ccc' : 'var(--color-primary-hover)',
                                                    transform: customerCards[index].disabled ? 'none' : 'scale(1.05)',
                                                    boxShadow: customerCards[index].disabled ? 'none' : { 
                                                        xs: 'none',
                                                        sm: 'none',
                                                        md: '0 5px 18px rgba(0, 0, 0, 0.25)', 
                                                        lg: '0 6px 20px rgba(0, 0, 0, 0.3)', 
                                                        xl: '0 8px 25px rgba(0, 0, 0, 0.35)' 
                                                    },
                                                },
                                                '&:active': {
                                                    transform: customerCards[index].disabled ? 'none' : 'scale(0.98)',
                                                }
                                            }}
                                        >
                                            {customerCards[index].loading ? (
                                                <CircularProgress size={22} color="inherit" />
                                            ) : (
                                                customerCards[index].buttonText
                                            )}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Layout>
    );
};

export default Welcome;
